import { useEffect, useState } from 'react'
import { ChatView, type ChatViewProps } from '../chat/ChatView'
import { ChatHeader, type HeaderPermissions } from './ChatHeader'
import type { ChatRow } from './chatRows'
import { AiStrip } from './phone/AiStrip'
import { PhoneChatHeader } from './phone/PhoneChatHeader'
import { useChatsDeps } from './ChatsDeps'
import { SideColumn } from './SideColumn'
import type { OpenChat } from './sideBlocks'

export interface ChatWindowProps extends Omit<ChatViewProps, 'renderHeader' | 'renderSide' | 'liveTick'> {
  /** What the person may change (writes are hidden otherwise). */
  can: HeaderPermissions
  /** The chat went away (hidden or cleared): leave it. */
  onLeave: () => void
  /** Phones: back to the list, drawn in the header. */
  onBack?: (() => void) | null
  /** Open a chat from the side column (this Account or another). */
  onOpenChat?: OpenChat
  /** Open the Workspace page (the dock widgets' expand button). */
  onOpenWorkspace?: () => void
  /** Phones (ADR 0035): the list's rows for the avatar rail and Back badge, and Mark as unread. */
  phone?: { siblings: ChatRow[]; onOpenSibling: (fanId: string, name: string) => void; onMarkUnread: (() => void) | null }
}

/**
 * The Chats page's open chat (ADR 0024): the React ChatView under the CRM chat
 * window's header, reloading its newest messages when this fan's chat changes
 * live, with the person's own side column (Fan panel blocks and widgets).
 * Workspace draws its own header and Fan panel around the same ChatView.
 */
export function ChatWindow({ can, onLeave, onBack = null, onOpenChat, onOpenWorkspace, phone, ...props }: ChatWindowProps) {
  const { accountId, fanId } = props
  const { subscribe } = useChatsDeps()
  const [tick, setTick] = useState(0)
  useEffect(
    () =>
      subscribe(accountId, (ev) => {
        if (ev.fan_id === fanId && ev.kind !== 'fan_lists' && ev.kind !== 'hot_enter') setTick((n) => n + 1)
      }),
    [subscribe, accountId, fanId],
  )
  return (
    <ChatView
      {...props}
      liveTick={tick}
      canWrite={can.writeChats}
      renderSide={(fan, frame) => (
        <SideColumn accountId={accountId} fanId={fanId} fan={fan} frame={frame} aiStatus={props.aiStatus} onOpenChat={onOpenChat} onOpenWorkspace={onOpenWorkspace} />
      )}
      renderHeader={(fan, chat) =>
        phone && onBack ? (
          <PhoneChatHeader
            accountId={accountId}
            fanId={fanId}
            fan={fan}
            chat={chat}
            nameHint={props.fanNameHint ?? null}
            can={can}
            onLeave={onLeave}
            onBack={onBack}
            siblings={phone.siblings}
            onOpenSibling={phone.onOpenSibling}
            onMarkUnread={phone.onMarkUnread}
          />
        ) : (
          <ChatHeader accountId={accountId} fanId={fanId} fan={fan} chat={chat} nameHint={props.fanNameHint ?? null} can={can} onLeave={onLeave} onBack={onBack} />
        )
      }
      renderBeforeComposer={phone ? (fan) => <AiStrip accountId={accountId} fanId={fanId} name={fan.profile?.name || props.fanNameHint || ''} /> : undefined}
    />
  )
}
